import type { Point } from "@deck.gl-community/editable-layers";
import { useMemo } from "react";
import { isValidCoordinates } from "../../../utils/geopatialUtils";
import { defaultMapFilterState, type MapFiltersState } from "../state";
import { deserializeMapStates } from "../utils/state";
import { useInitialSearchParams } from "./utils";

/**
 * Static helper to clean up URL parameters and perform
 * conversions and clean data.
 */
export const useInitialMapStateFromParams = () => {
    const initialParams = useInitialSearchParams([
        "mapState",
        "lat",
        "lon",
        "emissionId",
        "detectionDateRangeAfter",
        "detectionDateRangeBefore",
        "unmatchedDataPointId",
        "siteId",
    ]);
    const hasInitialState = useMemo(
        () => Object.values(initialParams).some((v) => v),
        [initialParams],
    );

    // Map state from URL -> when users use the share button.
    const deserializedState = useMemo(() => {
        if (initialParams.mapState) {
            try {
                return deserializeMapStates(initialParams.mapState);
            } catch {
                return undefined;
            }
        }
        return undefined;
    }, [initialParams.mapState]);

    // Lat and lon coordinates from URL
    const startingCoordinates = useMemo(() => {
        const latitude = parseFloat(initialParams.lat);
        const longitude = parseFloat(initialParams.lon);
        if (
            Number.isNaN(latitude) ||
            Number.isNaN(longitude) ||
            !isValidCoordinates(latitude, longitude)
        ) {
            return undefined;
        }
        return [longitude, latitude];
    }, [initialParams.lat, initialParams.lon]);

    // Date range parameters from URL
    const initialDateRange = useMemo(() => {
        if (
            !initialParams.detectionDateRangeAfter ||
            !initialParams.detectionDateRangeBefore
        )
            return undefined;
        const afterDate = new Date(initialParams.detectionDateRangeAfter);
        const beforeDate = new Date(initialParams.detectionDateRangeBefore);
        if (
            Number.isNaN(afterDate.getTime()) ||
            Number.isNaN(beforeDate.getTime())
        ) {
            return undefined;
        }
        return {
            startDateFilter: afterDate.toISOString(),
            endDateFilter: beforeDate.toISOString(),
        };
    }, [
        initialParams.detectionDateRangeAfter,
        initialParams.detectionDateRangeBefore,
    ]);

    return {
        hasInitialState,
        initialBasemap:
            deserializedState?.basemap ??
            localStorage.getItem("basemap") ??
            null,
        initialMapState: {
            longitude:
                deserializedState?.lon ?? startingCoordinates?.[0] ?? -98.58,
            latitude:
                deserializedState?.lat ?? startingCoordinates?.[1] ?? 39.82,
            zoom: deserializedState?.z ?? (startingCoordinates ? 15 : 4),
        },
        initialFilterState: (deserializedState
            ? {
                  emissions: deserializedState.emissions,
                  infrastructure: deserializedState.infrastructure,
              }
            : initialDateRange
              ? {
                    emissions: {
                        ...defaultMapFilterState.emissions,
                        ...initialDateRange,
                    },
                }
              : undefined) as Partial<MapFiltersState>,
        initialSelectedContext: {
            selectedContext: {
                ...(deserializedState?.mapData || {
                    emissionRecordId: initialParams.emissionId,
                    siteId: initialParams.siteId,
                    unmatchedDataPointId: initialParams.unmatchedDataPointId,
                }),
                selectedLocation: startingCoordinates
                    ? ({
                          type: "Point",
                          coordinates: startingCoordinates,
                      } as Point)
                    : undefined,
            },
        },
        initialEmissionId: initialParams.emissionId,
    };
};
